import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/auth/context';
import { readRedirectTarget } from '@/auth/redirect';
import { useT } from '@/i18n/context';

/**
 * `/login` — Google Sign-In (D8).
 *
 * Google's own rendered button, not a hand-rolled one: it is the only variant that works
 * across FedCM, ITP and the third-party-cookie rules, and re-implementing it earns nothing.
 *
 * This route is deliberately outside `RequireAuth`, which is what makes the guard
 * loop-proof.
 */
export function LoginRoute() {
  const t = useT();
  const { status, reason, identityStatus, identityError, signIn, renderButton } = useAuth();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);

  // Re-runs when GIS finishes loading: on a cold start the element exists long before
  // `google.accounts.id` does.
  useEffect(() => {
    if (identityStatus === 'ready') renderButton(buttonRef.current);
  }, [identityStatus, renderButton]);

  if (status === 'signed_in') {
    return <Navigate to={readRedirectTarget(location.state)} replace />;
  }

  const devSignIn = import.meta.env.DEV && !import.meta.env.VITE_GOOGLE_CLIENT_ID;

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-8">
      <h1 className="text-2xl font-semibold tracking-tight">{t('ui.auth.sign_in')}</h1>

      {/* Anthropic's Usage Policy (effective 2026-11-12) requires a consumer-facing AI to say
       * so before the first exchange. The play screen's action bar repeats it for a player
       * whose session never passes through here. */}
      <p className="text-fg-muted text-sm">{t('ui.app.ai_disclosure')}</p>

      {reason === 'expired' && (
        <p className="text-fg-muted text-sm">{t('ui.auth.session_expired')}</p>
      )}

      <div ref={buttonRef} className="min-h-11" />

      {identityStatus === 'unavailable' && (
        <p className="text-fg-muted font-mono text-sm">{identityError}</p>
      )}

      {devSignIn && (
        <button
          type="button"
          onClick={signIn}
          className="border-border rounded border px-4 py-2 text-sm"
        >
          Continue as a local developer
        </button>
      )}
    </main>
  );
}
